import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  ClientSideRowModelModule,
  RowSelectionModule,
  PaginationModule,
  colorSchemeDarkBlue,
  colorSchemeLight,
  themeMaterial,
  ValidationModule,
  ColumnAutoSizeModule,
  QuickFilterModule,
  DateFilterModule,
  TextFilterModule,
  NumberFilterModule,
  RowApiModule,
  LocaleModule,
  EventApiModule,
  GridApi,
  ColumnApiModule,
  SelectEditorModule,
  TextEditorModule,
  CustomEditorModule,
  CellStyleModule,
  NumberEditorModule,
} from "ag-grid-community";
import { debounce } from "lodash-es";

import { AgGridReact } from "ag-grid-react";
import clsx from "clsx";
import CellWithBackgroundRenderer from "../CustomCells/Renderers/CellWithBackgroundRenderer";
import CellWithButtonsRenderer from "../CustomCells/Renderers/CellWithButtonsRenderer";
import TableHeaderTogglePinned from "./TableHeaderTogglePinned";
import useDarkMode from "../../hooks/useDarkMode";
import useMobile from "../../hooks/useMobile";
import NoTableData from "../NoTableData/NoTableData";
import TableQuickFilter from "../TableQuickFilter/TableQuickFilter";
import useAccountLocalStorage from "src/hooks/useAccountLocalStorage";
import { PAGINATION_PAGE_SIZES } from "src/helpers/constants";
import TablePagination from "./TablePagination";
import useTablePreferences from "src/hooks/useTablePreferences";
import CustomSelectCellEditor from "./CellEditors/CustomSelectCellEditor";
import CustomTextInputCellEditor from "./CellEditors/CustomTextInputCellEditor";
import SlideDownUpAnimation from "../Animations/SlideDownUpAnimation";
import CellColorRenderer from "../CustomCells/Renderers/CellColorRenderer";

import {
  EAttachmentsTableKeys,
  ECategoriesTableKeys,
  EContactInvitesTableKeys,
  EPaymentMethodsTableKeys,
  EReportsTableKeys,
  ESourcesTableKeys,
  ETransactionsTableKeys,
} from "./tableIds";
import { IButton } from "../Button/Button";

const LIGHT_THEME_PARAMS = {
  backgroundColor: "#ffffff",
  // rowBorder: {
  //   color: "#f1f1f1",
  // },
  // columnBorder: { color: "#c0c0c0" },
};
const DARK_THEME_PARAMS = {
  backgroundColor: "#1f2937",
};

const MIN_PAGE_SIZE = Math.min(...PAGINATION_PAGE_SIZES);
const [MOBILE_DEFAULT_PAGE_SIZE, DESKTOP_DEFAULT_PAGE_SIZE] =
  PAGINATION_PAGE_SIZES;

interface ICustomSettings {
  resultsPerPage: number;
}

interface ICommonTable {
  data: any;
  columns: any;
  onSelectionChange: (rows: Array<any>) => void;
  customSettings?: ICustomSettings;
  singleSelection?: boolean;
  onEditStopped?: (data: any) => void;
  showSettings?: boolean;
  settingsKey:
    | EReportsTableKeys
    | ETransactionsTableKeys
    | EAttachmentsTableKeys
    | ESourcesTableKeys
    | EPaymentMethodsTableKeys
    | ECategoriesTableKeys
    | EContactInvitesTableKeys;
  noDataButtons?: IButton[];
}

const CommonTable = forwardRef<any, ICommonTable>(
  (
    {
      data,
      columns,
      onSelectionChange,
      customSettings = null,
      singleSelection = false,
      onEditStopped = () => undefined,
      showSettings = true,
      settingsKey,
      noDataButtons = [],
    },
    ref
  ) => {
    const [gridApi, setGridApi] = useState<GridApi<any> | null>(null);
    const [hasVisibleRows, setHasVisibleRows] = useState(true);

    useImperativeHandle(ref, () => ({
      getGridApi: () => gridApi,
    }));

    const { isEnabled: isDarkMode } = useDarkMode();
    const { isMobile } = useMobile();
    const [gridReady, setGridReady] = useState(false);
    const { accountLocalStoragePreferences, updateAccountLocalStorage } =
      useAccountLocalStorage(settingsKey);
    const {
      hasBottomPagination,
      hasDoublePaginationEnabled,
      hasTopPagination,
      allowMultipleSelection,
      selectOnRowClick,
      allowPinnedColumns,
      showColumnFilters,
    } = useTablePreferences();

    const onRowClicked = useCallback(
      (event: any) => {
        if (selectOnRowClick) {
          if (!singleSelection) {
            if (!allowMultipleSelection) {
              event?.api?.deselectAll?.();
            }
            const isSelected = event.node.isSelected();
            event.node.setSelected(!isSelected);
          }
        }
      },
      [allowMultipleSelection, selectOnRowClick]
    );

    const [searchTerm, setSearchTerm] = useState<string>(
      accountLocalStoragePreferences?.quickFilterText || ""
    );

    const myTheme = useMemo(() => {
      return themeMaterial
        .withPart(isDarkMode ? colorSchemeDarkBlue : colorSchemeLight)
        .withParams({
          ...(isDarkMode ? DARK_THEME_PARAMS : LIGHT_THEME_PARAMS),
        });
    }, [isDarkMode]);

    const applyFilter = useCallback(
      debounce((term: string) => {
        if (!gridApi) return;
        gridApi?.setGridOption?.("quickFilterText", term);
      }, 300),
      [gridApi]
    );

    const applyFilterImmediate = useCallback(
      (term: string) => {
        if (!gridApi) return;
        gridApi?.setGridOption?.("quickFilterText", term);
      },
      [gridApi]
    );

    const onFilterChanged = useCallback(() => {
      if (!gridApi) return;
      const rowCount = gridApi?.getDisplayedRowCount?.() || 0;
      setHasVisibleRows(rowCount > 0);
    }, [gridApi]);

    useEffect(() => {
      if (!gridApi) return;
      applyFilter(searchTerm);
      updateAccountLocalStorage({ quickFilterText: searchTerm });
    }, [searchTerm, applyFilter, gridApi]);

    const onModelUpdated = useCallback(() => {
      if (!gridApi) return;
      const rowCount = gridApi.getDisplayedRowCount?.() || 0;
      setHasVisibleRows(rowCount > 0);
    }, [gridApi]);

    const onFirstDataRendered = useCallback(() => {
      if (gridApi) {
        const columnIds = gridApi
          ?.getAllGridColumns?.()
          ?.map?.((col: any) => col.getId());
        if (columnIds) {
          gridApi?.autoSizeColumns?.(columnIds, false);
        }
        const savedPage = accountLocalStoragePreferences?.page ?? 0;
        const totalPages = gridApi.paginationGetTotalPages?.();
        if (totalPages && savedPage < totalPages) {
          gridApi.paginationGoToPage(savedPage);
        } else {
          gridApi.paginationGoToPage(0);
        }

        onFilterChanged();
      }
    }, [gridApi, accountLocalStoragePreferences?.page, onFilterChanged]);

    const onPaginationChanged = useCallback(() => {
      if (!gridApi) return;
      if (!customSettings?.resultsPerPage) {
        updateAccountLocalStorage({
          resultsPerPage: gridApi?.paginationGetPageSize?.(),
        });
      }
      updateAccountLocalStorage({
        page: gridApi?.paginationGetCurrentPage?.(),
      });
    }, [gridApi]);

    useEffect(() => {
      if (!isMobile) {
        window.addEventListener("resize", onFirstDataRendered);
        return () => window.removeEventListener("resize", onFirstDataRendered);
      }
    }, [gridApi, isMobile]);

    const resultsPerPage = useMemo(() => {
      return (
        customSettings?.resultsPerPage ||
        accountLocalStoragePreferences?.resultsPerPage ||
        (isMobile ? MOBILE_DEFAULT_PAGE_SIZE : DESKTOP_DEFAULT_PAGE_SIZE)
      );
    }, [
      customSettings?.resultsPerPage,
      accountLocalStoragePreferences?.resultsPerPage,
    ]);

    const hasFilter = useMemo(() => {
      return gridApi?.isAnyFilterPresent?.() ?? false;
    }, [gridApi]);

    const totalRows = useMemo(() => {
      if (!gridApi) return data?.length || 0;
      if (hasFilter) {
        return gridApi?.getDisplayedRowCount?.() || 0;
      }
      const rowData = [];
      gridApi?.forEachNode((node: any) => rowData.push(node.data));
      return rowData?.length || 0;
    }, [hasFilter, data?.length, gridApi]);

    const hidePagination = useMemo(() => {
      if (hasFilter) {
        return totalRows === 0 ? true : totalRows <= resultsPerPage;
      }
      return totalRows === 0 ? true : data?.length <= resultsPerPage;
    }, [hasFilter, totalRows, resultsPerPage, data?.length, MIN_PAGE_SIZE]);

    const modifiedColumns = useMemo(() => {
      if ([allowPinnedColumns, showColumnFilters].includes(false)) {
        return columns?.map((col: any) => ({
          ...col,
          ...(!allowPinnedColumns
            ? { pinned: null, headerComponentParams: null }
            : {}),
          ...(showColumnFilters ? {} : { filter: null }),
        }));
      } else {
        return columns;
      }
    }, [allowPinnedColumns, columns, showColumnFilters]);

    return (
      <div
        className={clsx(["relative", "block", "w-full"])}
        data-ag-theme-mode={isDarkMode ? "dark-blue" : "light"}
      >
        <div className={clsx(["flex", "w-full", "pb-4"])}>
          <TableQuickFilter searchTerm={searchTerm} onChange={setSearchTerm} />
        </div>

        <SlideDownUpAnimation
          show={
            [hasDoublePaginationEnabled, hasTopPagination].includes(true) &&
            hasVisibleRows
          }
        >
          <TablePagination
            gridApi={gridApi}
            resultsPerPage={resultsPerPage}
            noBorder
            key={searchTerm}
            showSettings={showSettings}
          />
        </SlideDownUpAnimation>

        <AgGridReact
          theme={myTheme}
          rowData={data}
          onModelUpdated={onModelUpdated}
          noRowsOverlayComponent={() => null}
          columnDefs={modifiedColumns}
          defaultColDef={{}}
          suppressMovableColumns
          key={`${allowPinnedColumns}-${showColumnFilters}-${settingsKey}`}
          onFilterChanged={onFilterChanged}
          gridOptions={{
            suppressMovableColumns: true,
            paginationPageSize: resultsPerPage,
            rowSelection: {
              mode: singleSelection ? "singleRow" : "multiRow",
              checkboxes: !singleSelection,
              enableClickSelection: singleSelection,
            },
          }}
          onPaginationChanged={onPaginationChanged}
          onFirstDataRendered={onFirstDataRendered}
          selectionColumnDef={
            singleSelection
              ? {}
              : {
                  pinned: !isMobile,
                  sortable: false,
                  resizable: false,
                  suppressHeaderMenuButton: true,
                  width: 70,
                  maxWidth: 70,
                  minWidth: 70,
                  lockPosition: "left",
                }
          }
          suppressCellFocus
          suppressColumnVirtualisation={false}
          suppressGroupRowsSticky
          suppressPaginationPanel
          components={{
            CellWithBackgroundRenderer,
            CellWithButtonsRenderer,
            CellColorRenderer,
            TablePagination,
            TableHeaderTogglePinned,
            CustomSelectCellEditor,
            CustomTextInputCellEditor,
          }}
          onGridReady={({ api }) => {
            setGridApi(api);
            setGridReady(true);
            applyFilterImmediate(searchTerm);
            setTimeout(() => {
              const savedPage = accountLocalStoragePreferences?.page ?? 0;
              const totalPages = api.paginationGetTotalPages?.();
              if (totalPages && savedPage < totalPages) {
                api.paginationGoToPage(savedPage);
              } else {
                api.paginationGoToPage(0);
              }
            }, 0);
          }}
          className={clsx(["w-full", "!h-auto md:!h-auto"])}
          onRowClicked={onRowClicked}
          onCellEditingStopped={onEditStopped}
          singleClickEdit
          modules={[
            SelectEditorModule,
            ClientSideRowModelModule,
            PaginationModule,
            RowSelectionModule,
            ValidationModule,
            ColumnAutoSizeModule,
            QuickFilterModule,
            DateFilterModule,
            TextFilterModule,
            NumberFilterModule,
            RowApiModule,
            LocaleModule,
            EventApiModule,
            ColumnApiModule,
            TextEditorModule,
            CustomEditorModule,
            CellStyleModule,
            NumberEditorModule,
          ]}
          onSelectionChanged={() =>
            onSelectionChange(gridApi?.getSelectedRows?.() || [])
          }
        />

        {/* Custom overlay to persist even on scroll */}
        {!hasVisibleRows && <NoTableData buttons={noDataButtons} />}

        <SlideDownUpAnimation
          show={
            [hasDoublePaginationEnabled, hasBottomPagination].includes(true) &&
            hasVisibleRows
          }
        >
          <TablePagination
            showSettings={showSettings}
            gridApi={gridApi}
            resultsPerPage={resultsPerPage}
          />
        </SlideDownUpAnimation>
      </div>
    );
  }
);

export default React.memo(CommonTable);
